import React, { FC, useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import {
  Checkbox,
  CheckboxProps,
  Input,
  Link,
  PasswordInput,
  Typography,
} from '@loomstateorg/shared-frontend';
import Button from 'components/commercetools-ui/Button';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import { useAccount, useCart, useProduct } from 'frontastic';
import { NextFrontasticImage } from 'frontastic/lib/image';
import Feedback from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/account/account-atoms/feedback';
import {
  AuthLook,
  CAVA_AUTH_LOOK,
} from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/authentication/auth-look';

interface Props {
  onLogin?: () => void;
  logo: NextFrontasticImage;
  src: string;
  look?: AuthLook;
}

const LoginForm: FC<Props> = ({ onLogin, logo, src, look = CAVA_AUTH_LOOK }) => {
  //i18n messages
  const { formatMessage: formatErrorMessage } = useFormat({ name: 'error' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });
  const { formatMessage } = useFormat({ name: 'common' });

  const { getCart, cart } = useCart();
  const { getAllCategories } = useProduct();

  //account actions
  const { login, requestConfirmationEmail, requestPasswordReset } = useAccount();

  //login data
  const [data, setData] = useState({ email: '', password: '', rememberMe: false });

  //error
  const [error, setError] = useState('');

  //success
  const [success, setSuccess] = useState('');

  //processing...
  const [loading, setLoading] = useState(false);

  //attempting to resend verification email
  const [resendVerification, setResendVerification] = useState(false);

  //attempting to request a password reset
  const [resendPasswordReset, setResendPasswordReset] = useState(false);

  //not on default login modal
  const subModal = resendVerification || resendPasswordReset;

  const resetFeedback = () => {
    setError('');
    setSuccess('');
  };

  //get back to login modal
  const backToLogin = () => {
    setResendPasswordReset(false);
    setResendVerification(false);
    resetFeedback();
  };

  //requesting a password reset
  const toResendPassword = () => {
    setResendPasswordReset(true);
    setResendVerification(false);
    resetFeedback();
  };

  //handle text input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  //handle checkbox input change
  const handleCheckboxChange: CheckboxProps['onChange'] = ({ name, checked }) => {
    setData({ ...data, [name]: checked });
  };

  //login user
  const loginUser = async () => {
    try {
      const response = await login(data.email, data.password, data.rememberMe);

      if (response.accountId) {
        getCart();
        getAllCategories();
        onLogin?.();
      } else setError(formatErrorMessage({ id: 'auth.wrong', defaultMessage: 'Wrong email address or password' }));
    } catch (err) {
      setError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
    }
  };

  //resend verification email for user
  const resendVerificationEmailForUser = async () => {
    try {
      await requestConfirmationEmail(data.email, data.password);
      setSuccess(
        formatAccountMessage({
          id: 'verification.resent',
          defaultMessage: 'An email was sent to {email}',
          values: { email: data.email },
        }),
      );
    } catch (err) {
      setError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
    }
  };

  //request a password reset for user
  const resendPasswordResetForUser = async () => {
    try {
      const response = await requestPasswordReset(data.email);
      if (!response.isError) {
        resetFeedback();
        setSuccess(
          formatAccountMessage({
            id: 'verification.resent',
            defaultMessage: 'An email was sent to {email}',
            values: { email: data.email },
          }),
        );
      } else {
        resetFeedback();
        setError(
          formatAccountMessage({
            id: 'email.not.found',
            defaultMessage: `Email not found, please check there's no spelling errors or typos`,
          }),
        );
      }
    } catch (err) {
      setError(formatErrorMessage({ id: 'wentWrong', defaultMessage: 'Sorry. Something went wrong..' }));
    }
  };

  //form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    //processing starts
    setLoading(true);
    //if user is attempting to resend verification email
    if (resendVerification) resendVerificationEmailForUser();
    //if user is attempting tor equest a password reset
    else if (resendPasswordReset) resendPasswordResetForUser();
    //if user wants to login
    else loginUser();
    //processing ends
    setLoading(false);
  };
  return (
    <>
      <div className="relative px-10 pb-36 md:mt-0">
        <img src={src} alt={logo?.title} />
      </div>
      <form onSubmit={handleSubmit} data-testid="login-form">
        <Feedback success={success} error={error} />

        <Input
          data-testid="email-input"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          required
              className={look.inputClassName}
              placeholder={formatMessage({ id: 'emailAddress', defaultMessage: 'Email Address' })}
          onChange={handleChange}
        />

        {!resendPasswordReset && (
          <PasswordInput
            data-testid="password-input"
            required
            id="password"
            name="password"
            autoComplete="current-password"
            placeholder={formatAccountMessage({ id: 'password', defaultMessage: 'Password' })}
            className={look.inputClassName}
            toggleClassName="top-25"
            onChange={handleChange}
          />
        )}

        {subModal ? (
          <div>
            <ArrowLeftIcon
              className="w-4 cursor-pointer"
              onClick={backToLogin}
              data-testid="back"
              aria-label={formatAccountMessage({ id: 'account.back.sign', defaultMessage: 'Back to sign in' })}
            />
          </div>
        ) : (
            <div className={look.rememberRowClassName}>
            <Checkbox
              data-testid="remember-me-toggle"
              id="remember-me"
              name="rememberMe"
              onChange={handleCheckboxChange}
              label={formatMessage({ id: 'saveId', defaultMessage: 'Save Online ID' })}
            />
          </div>
        )}

        <Button
          size="full"
          type="submit"
          className={look.submitClassName}
          disabled={loading}
          data-component-group="button"
          data-testid="submit"
        >
          {resendPasswordReset
            ? formatAccountMessage({ id: 'account.reset.link', defaultMessage: 'Get reset link' })
            : formatAccountMessage({ id: 'signin', defaultMessage: 'SIGN IN' })}
        </Button>
        {!resendPasswordReset && (
          <Typography
            className={look.forgotPasswordClassName}
            fontSize={12}
            onClick={toResendPassword}
            data-testid="forgot-password"
          >
            {formatAccountMessage({ id: 'password.forgot', defaultMessage: 'Forgot your password?' })}
          </Typography>
        )}

        {resendPasswordReset && (
          <Link
            variant="menu-item"
            className="mx-auto block w-fit cursor-pointer text-14"
            link=""
            onClick={backToLogin}
            data-testid="back-to-sign-in"
          >
            {formatAccountMessage({ id: 'account.back.sign', defaultMessage: 'Back to sign in' })}
          </Link>
        )}
      </form>
    </>
  );
};

export default LoginForm;
